import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, PageHeader, Stat, StatusPill, Empty } from "../../components/dash/Kit";
import { api } from "../../lib/api";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [revisions, setRevisions] = useState([]);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setStats(r.data)).catch(() => setStats(false));
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/admin/revisions", { params: { status: "open" } }).then((r) => setRevisions(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <PageHeader title="Admin Overview" body="Live figures from the database. No estimates or placeholder numbers." />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testId="admin-stat-customers" label="Active Customers" value={stats?.active_customers ?? "—"} />
        <Stat testId="admin-stat-mrr" label="Monthly Recurring Revenue" value={stats ? `€${stats.mrr}` : "—"} hint="excl. VAT" />
        <Stat testId="admin-stat-new" label="New Customers This Month" value={stats?.new_customers_this_month ?? "—"} />
        <Stat testId="admin-stat-cancelled" label="Cancelled Subscriptions" value={stats?.cancelled_subscriptions ?? "—"} />
        <Stat testId="admin-stat-content" label="Content Items Created" value={stats?.content_created ?? "—"} />
        <Stat testId="admin-stat-revisions" label="Revision Requests" value={stats?.revision_requests ?? "—"} />
        <Stat testId="admin-stat-tickets" label="Open Support Tickets" value={stats?.open_tickets ?? "—"} />
        <Stat testId="admin-stat-open-revisions" label="Open Revisions" value={revisions.length} />
      </div>

      <Card testId="admin-recent-customers">
        <div className="flex items-center justify-between">
          <p className="font-display text-lg font-semibold text-slate-900">Recent customers</p>
          <Link to="/admin/customers" className="text-sm font-semibold text-[#F95738] hover:underline">View all</Link>
        </div>
        {customers.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">No customers yet.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {customers.slice(0, 6).map((c) => (
              <Link
                key={c.id}
                to={`/admin/customers/${c.id}`}
                data-testid={`admin-recent-customer-${c.id}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 px-4 py-3 hover:border-slate-300"
              >
                <div>
                  <p className="text-sm font-semibold text-slate-900">{c.business_name}</p>
                  <p className="text-xs text-slate-500">{c.first_name} {c.last_name} · {c.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                    {c.subscription?.package || "—"}
                  </span>
                  <StatusPill status={c.profile_completed ? "Completed" : "Information Required"} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>

      <Card testId="admin-open-revisions">
        <p className="font-display text-lg font-semibold text-slate-900">Open revision requests</p>
        {revisions.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">Nothing waiting.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {revisions.slice(0, 5).map((r) => (
              <div key={r.id} className="rounded-xl border border-slate-200 px-4 py-3">
                <p className="text-sm font-semibold text-slate-900">{r.customer?.business_name}</p>
                <p className="mt-1 text-sm text-slate-600">{r.description}</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {stats && Object.keys(stats.industries || {}).length > 0 && (
        <Card testId="admin-industries">
          <p className="font-display text-lg font-semibold text-slate-900">Most common industries</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.entries(stats.industries).map(([k, v]) => (
              <span key={k} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700">
                {k}: {v}
              </span>
            ))}
          </div>
        </Card>
      )}

      {stats === false && <Empty testId="admin-analytics-error" title="Could not load analytics" />}
    </div>
  );
}
